import { Eye, RefreshCw, Send } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api';
import type { TrackingCounts, TrackingReport } from '../../../api/areas/postOperations';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../../components/blocks';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, ErrorState, Loading, TechnicalDetail } from '../../../components/States';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { errorTextWith } from '../../../lib/apiErrorText';
import { useApi } from '../../../lib/useApi';
import { ActionDialog, FormActions, MfaHint, SuccessNote, When } from '../ui';
import { useWrite, WriteError } from './shared';

const TONE: Record<TrackingReport['status'], Tone> = {
  PENDING: 'pending',
  SENDING: 'info',
  SENT: 'ok',
  RETRY_WAIT: 'warn',
  FAILED: 'crit',
};

/** HostBill / VNNIC field names, in the order the report lists them: per channel, requests then issued. */
export const CHANNELS: { key: 'available' | 'bizFree' | 'idFree' | 'suggested'; request: keyof TrackingCounts; issued: keyof TrackingCounts }[] = [
  { key: 'available', request: 'availableDomainRequest', issued: 'availableDomainIssued' },
  { key: 'bizFree', request: 'bizFreeRequest', issued: 'bizFreeIssued' },
  { key: 'idFree', request: 'idFreeRequest', issued: 'idFreeIssued' },
  { key: 'suggested', request: 'suggestedDomainRequest', issued: 'suggestedDomainIssued' },
];

/** The eight counts as a small table: one row per channel, requests and issued names. */
function Counts({ counts }: { counts: TrackingCounts | null }) {
  const { t } = useTranslation('compliance');
  if (!counts) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="grid w-max grid-cols-[auto_auto_auto] gap-x-3 text-xs tabular-nums">
      <span />
      <span className="text-right text-muted-foreground">{t('tracking.requests')}</span>
      <span className="text-right text-muted-foreground">{t('tracking.issued')}</span>
      {CHANNELS.map((c) => (
        <Fragment key={c.key}>
          <span className="whitespace-nowrap text-muted-foreground">{t(`tracking.channel.${c.key}`)}</span>
          <span className="text-right" title={t(`tracking.field.${c.request}`)}>
            {counts[c.request]}
          </span>
          <span className="text-right" title={t(`tracking.field.${c.issued}`)}>
            {counts[c.issued]}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

/** Why a day is not sent, in the operator's words: VNNIC's answer (English, codes) is the folded detail. */
function LastError({ report }: { report: TrackingReport }) {
  const { t, i18n } = useTranslation('compliance');
  if (!report.last_error) return null;
  const tr = t as unknown as (key: string, options: { defaultValue: string }) => string;
  // `apiErrors.status.<kind>` of the shared reader -> `tracking.error.<kind>`.
  const lookup = (key: string) => tr(`tracking.error.${key.split('.').pop()}`, { defaultValue: '' }) || undefined;
  // Not sent: nothing reached VNNIC (the Registrar API is off); the client's reason is technical.
  const shown =
    report.status === 'PENDING'
      ? { text: t('tracking.notSent'), detail: report.last_error }
      : errorTextWith({ code: '', status: report.http_status ?? -1, message: report.last_error }, lookup, i18n.language.startsWith('vi'));
  return (
    <span className="grid gap-0.5 text-xs text-muted-foreground">
      {shown.text}
      <TechnicalDetail detail={shown.detail} />
    </span>
  );
}

/** "Gửi lại": the day's counts as they would be sent now, then send. */
function ResendForm({ date, onSent, onClose }: { date: string; onSent: () => void; onClose: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const preview = useApi((signal) => api.previewTrackingReport(date, { signal }), [date]);
  const send = useWrite((_: null, opts) => api.sendTrackingReport(date, opts));
  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">{t('tracking.resendBody', { date })}</p>
      {preview.status === 'loading' ? <Loading rows={2} /> : null}
      {preview.status === 'error' ? <ErrorState error={preview.error} onRetry={preview.reload} title={t('tracking.previewError')} /> : null}
      {preview.status === 'success' ? <Counts counts={preview.data.counts} /> : null}
      <MfaHint permission="regulatory_report.manage" />
      {send.state.phase === 'error' ? <WriteError error={send.state.error} /> : null}
      {send.state.phase === 'done' ? (
        <SuccessNote>{t('tracking.sent', { date, method: send.state.result.report.method ?? 'POST' })}</SuccessNote>
      ) : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onClose} disabled={send.busy}>
          {send.state.phase === 'done' ? tc('action.close') : tc('action.cancel')}
        </Button>
        {send.state.phase === 'done' ? null : (
          <Button
            type="button"
            disabled={send.busy || preview.status !== 'success'}
            onClick={async () => {
              // The list shows the new status at once, however the dialog is closed.
              if (await send.run(null)) onSent();
            }}
          >
            <Send /> {t('tracking.resend')}
          </Button>
        )}
      </FormActions>
    </div>
  );
}

/**
 * VNNIC daily Domain Tracking Report (ADR-024; the HostBill `sendDailyDomainTrackingReport`):
 * the previous day's .vn requests and issued names per channel, sent once a day with the VNNIC
 * Registrar API settings; status and attempts per day, "Gửi lại" for any past day.
 */
export function TrackingReportsPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  useDocumentTitle(t('tracking.title'));
  const [version, setVersion] = useState(0);
  const list = useApi((signal) => api.listTrackingReports({ limit: 60 }, { signal }), [version]);
  const [resend, setResend] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const refresh = () => setVersion((v) => v + 1);

  const columns: Column<TrackingReport>[] = [
    { key: 'date', header: t('tracking.col.date'), cell: (r) => <span className="whitespace-nowrap tabular-nums">{r.stats_date}</span> },
    {
      key: 'status',
      header: t('tracking.col.status'),
      cell: (r) => (
        <span className="grid max-w-60 gap-0.5">
          <Badge tone={TONE[r.status]}>{t(`tracking.status.${r.status}`)}</Badge>
          {r.status === 'RETRY_WAIT' && r.next_attempt_at ? (
            <span className="text-xs text-muted-foreground">
              {t('tracking.nextAttempt')} <When at={r.next_attempt_at} />
            </span>
          ) : null}
          <LastError report={r} />
        </span>
      ),
    },
    { key: 'counts', header: t('tracking.col.counts'), cell: (r) => <Counts counts={r.counts} /> },
    {
      key: 'sent',
      header: t('tracking.col.sent'),
      cell: (r) => (
        <span className="grid text-xs">
          {r.sent_at ? <When at={r.sent_at} /> : <span className="text-muted-foreground">—</span>}
          {r.sent_at ? <span className="text-muted-foreground">{r.method === 'PUT' ? t('tracking.corrected') : t('tracking.created')}</span> : null}
          <span className="text-muted-foreground">{t('tracking.attemptsCount', { count: r.attempts })}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <Button size="sm" variant="outline" onClick={() => setResend(r.stats_date)} disabled={r.status === 'SENDING'}>
          <Send /> {t('tracking.resend')}
        </Button>
      ),
    },
  ];

  const data = list.status === 'success' ? list.data : null;
  return (
    <>
      <PageHeader
        title={t('tracking.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('tracking.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={refresh}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        <Callout tone="info" title={t('tracking.howTitle')}>
          <p>{t('tracking.how')}</p>
          <p className="text-xs text-muted-foreground">{t('tracking.rules')}</p>
        </Callout>
        {data && !data.configured ? (
          <Callout tone="warn" title={t('tracking.notConfiguredTitle')}>
            <p>{t('tracking.notConfigured')}</p>
          </Callout>
        ) : null}
        <Section title={t('tracking.otherDay')}>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (other) setResend(other);
            }}
          >
            <Field label={t('tracking.col.date')}>
              <Input type="date" value={other} max={data?.default_stats_date} onChange={(e) => setOther(e.target.value)} className="w-44" />
            </Field>
            <Button type="submit" size="sm" variant="outline" disabled={!other}>
              <Eye /> {t('tracking.previewAndSend')}
            </Button>
          </form>
        </Section>
        <Section flush title={t('tracking.daysTitle')}>
          {list.status === 'loading' ? <Loading label={t('tracking.loading')} rows={3} /> : null}
          {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} title={t('tracking.loadError')} /> : null}
          {data ? <DataTable columns={columns} rows={data.items} rowKey={(r) => r.id} empty={<EmptyState title={t('tracking.empty')} />} /> : null}
        </Section>
      </div>
      <ActionDialog open={resend !== null} onOpenChange={(o) => (o ? undefined : setResend(null))} title={t('tracking.resendTitle', { date: resend ?? '' })}>
        {resend ? (
          <ResendForm
            key={resend}
            date={resend}
            onClose={() => setResend(null)}
            onSent={refresh}
          />
        ) : null}
      </ActionDialog>
    </>
  );
}
